/**
 * The 3D overlay layer (VLP-2300 WP-B): a MapLibre CustomLayerInterface drawing track3d meshes at real
 * heights. MapLibre v5 ignores z in line/fill coordinates, so the elevated aircraft, drop lines, ribbon
 * and cone are drawn here in web-mercator space (MercatorCoordinate.fromLngLat(…, metres)), relative to
 * a local origin so float32 precision holds at street zoom.
 */
import { type CustomLayerInterface, type CustomRenderMethodInput, type Map as MlMap } from 'maplibre-gl';
import type { Mesh } from './track3d';


const VS = `
attribute vec3 a_pos;
attribute vec4 a_col;
uniform mat4 u_matrix;
varying vec4 v_col;
void main() {
  gl_Position = u_matrix * vec4(a_pos, 1.0);
  v_col = a_col;
}`;
const FS = `
precision mediump float;
varying vec4 v_col;
void main() {
  gl_FragColor = v_col;
}`;

type GL = WebGLRenderingContext | WebGL2RenderingContext;

// ── web-mercator, inlined (identical formulas to MapLibre's MercatorCoordinate.fromLngLat; no allocation) ──
const EARTH_RADIUS = 6371008.8; // maplibre-gl earthRadius
const EARTH_CIRCUMFERENCE = 2 * Math.PI * EARTH_RADIUS;
export const mercX = (lng: number): number => (180 + lng) / 360;
export const mercY = (lat: number): number => (180 - ((180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)))) / 360;
/** metres of altitude → mercator z units at `lat` */
export const mercZ = (metres: number, lat: number): number => metres / (EARTH_CIRCUMFERENCE * Math.cos((lat * Math.PI) / 180));

/** Id of the per-frame mesh set by setMesh. */
const DYNAMIC = '';

/** One independently-offsettable batch of triangles with its own GL buffers (created lazily in render). */
interface Chunk {
  pos: Float32Array; // capacity ≥ count*3, reused across rebuilds
  col: Float32Array; // capacity ≥ count*4
  count: number;
  origin: [number, number, number];
  /** mercator of the lng/lat the chunk was built at (null = not movable) and the current shift from it */
  anchor: [number, number] | null;
  shift: [number, number];
  dirty: boolean;
  posBuf: WebGLBuffer | null;
  colBuf: WebGLBuffer | null;
}

export class Track3dLayer implements CustomLayerInterface {
  readonly id: string;
  readonly type = 'custom' as const;
  readonly renderingMode = '3d' as const;
  private map: MlMap | null = null;
  private gl: GL | null = null;
  private program: WebGLProgram | null = null;
  private chunks = new Map<string, Chunk>();
  private matrix: Float64Array | null = null;
  private mv = new Float32Array(16);
  private aPos = -1;
  private aCol = -1;
  private uMatrix: WebGLUniformLocation | null = null;
  private canvas: HTMLCanvasElement | null = null;

  constructor(id = 'apos-3d') {
    this.id = id;
  }

  onAdd(map: MlMap, gl: GL): void {
    // A style swap with {diff:false} re-adds this same instance without onRemove: free whatever the
    // previous add (possibly on a different context) left behind.
    this.release();
    this.unlisten();
    this.map = map;
    this.init(gl);
    this.canvas = map.getCanvas();
    this.canvas.addEventListener('webglcontextlost', this.onContextLost);
  }

  onRemove(_map?: MlMap, _gl?: GL): void {
    this.release();
    this.unlisten();
    this.map = null;
    this.matrix = null;
  }

  private unlisten(): void {
    this.canvas?.removeEventListener('webglcontextlost', this.onContextLost);
    this.canvas = null;
  }

  /** The context's resources are gone with it: forget the handles (do not delete), keep the geometry; render re-inits. */
  private onContextLost = (): void => {
    this.program = null;
    this.gl = null;
    for (const c of this.chunks.values()) {
      c.posBuf = c.colBuf = null;
      c.dirty = true;
    }
  };

  private init(gl: GL): void {
    const sh = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    };
    const vs = sh(gl.VERTEX_SHADER, VS);
    const fs = sh(gl.FRAGMENT_SHADER, FS);
    const p = gl.createProgram()!;
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    // the linked program keeps what it needs; the shader objects would otherwise leak
    gl.detachShader(p, vs);
    gl.detachShader(p, fs);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    this.gl = gl;
    this.program = p;
    this.aPos = gl.getAttribLocation(p, 'a_pos');
    this.aCol = gl.getAttribLocation(p, 'a_col');
    this.uMatrix = gl.getUniformLocation(p, 'u_matrix');
    for (const c of this.chunks.values()) c.dirty = true; // geometry survives a re-add; buffers are made on demand
  }

  /** Delete every GL object this layer owns on the context it created them on. Idempotent. */
  private release(): void {
    const gl = this.gl;
    if (gl) {
      for (const c of this.chunks.values()) {
        if (c.posBuf) gl.deleteBuffer(c.posBuf);
        if (c.colBuf) gl.deleteBuffer(c.colBuf);
      }
      if (this.program) gl.deleteProgram(this.program);
    }
    for (const c of this.chunks.values()) {
      c.posBuf = c.colBuf = null;
      c.dirty = true;
    }
    this.program = null;
    this.gl = null;
  }

  /** Replace the per-frame mesh (lng/lat/metres → local mercator). */
  setMesh(m: Mesh): void {
    this.setChunk(DYNAMIC, m);
  }

  /**
   * Replace one named batch. With `anchor`, the batch can later be translated cheaply with moveChunk
   * (the shift is measured from that lng/lat) instead of being rebuilt. null/empty mesh removes content.
   */
  setChunk(id: string, m: Mesh | null, anchor?: { lng: number; lat: number }): void {
    let c = this.chunks.get(id);
    const n = m ? m.pos.length / 3 : 0;
    if (!c) {
      if (n === 0) return;
      c = { pos: new Float32Array(0), col: new Float32Array(0), count: 0, origin: [0, 0, 0], anchor: null, shift: [0, 0], dirty: false, posBuf: null, colBuf: null };
      this.chunks.set(id, c);
    }
    if (m && n > 0) {
      if (c.pos.length < n * 3) c.pos = new Float32Array(Math.ceil(n * 1.5) * 3);
      if (c.col.length < n * 4) c.col = new Float32Array(Math.ceil(n * 1.5) * 4);
      const mp = m.pos;
      const ox = mercX(mp[0]!);
      const oy = mercY(mp[1]!);
      c.origin[0] = ox;
      c.origin[1] = oy;
      c.origin[2] = 0;
      const pos = c.pos;
      for (let i = 0; i < n; i++) {
        const lng = mp[i * 3]!;
        const lat = mp[i * 3 + 1]!;
        pos[i * 3] = mercX(lng) - ox;
        pos[i * 3 + 1] = mercY(lat) - oy;
        pos[i * 3 + 2] = mercZ(mp[i * 3 + 2]!, lat);
      }
      c.col.set(m.col);
    }
    c.count = n;
    c.anchor = anchor && n > 0 ? [mercX(anchor.lng), mercY(anchor.lat)] : null;
    c.shift[0] = c.shift[1] = 0;
    c.dirty = n > 0;
    this.map?.triggerRepaint();
  }

  /** Translate a batch built with an anchor so that anchor now sits at lng/lat. Cheap: no geometry or upload. */
  moveChunk(id: string, lng: number, lat: number): void {
    const c = this.chunks.get(id);
    if (!c || !c.anchor) return;
    c.shift[0] = mercX(lng) - c.anchor[0];
    c.shift[1] = mercY(lat) - c.anchor[1];
  }

  /** Drop every named batch that is not in `keep` (the per-frame mesh is kept). */
  pruneChunks(keep: ReadonlySet<string>): void {
    for (const [id, c] of this.chunks) {
      if (id === DYNAMIC || keep.has(id)) continue;
      this.freeChunk(c);
      this.chunks.delete(id);
    }
  }

  /** Remove all content. */
  clear(): void {
    for (const c of this.chunks.values()) this.freeChunk(c);
    this.chunks.clear();
    this.map?.triggerRepaint();
  }

  private freeChunk(c: Chunk): void {
    if (this.gl) {
      if (c.posBuf) this.gl.deleteBuffer(c.posBuf);
      if (c.colBuf) this.gl.deleteBuffer(c.colBuf);
    }
    c.posBuf = c.colBuf = null;
  }

  /** Screen point (CSS px) of lng/lat at `metres` height, from the last rendered camera; null if not drawn yet or behind the camera. */
  project(lon: number, lat: number, metres: number): { x: number; y: number } | null {
    const M = this.matrix;
    const map = this.map;
    if (!M || !map) return null;
    const cx = mercX(lon);
    const cy = mercY(lat);
    const cz = mercZ(metres, lat);
    const x = M[0]! * cx + M[4]! * cy + M[8]! * cz + M[12]!;
    const y = M[1]! * cx + M[5]! * cy + M[9]! * cz + M[13]!;
    const w = M[3]! * cx + M[7]! * cy + M[11]! * cz + M[15]!;
    if (!(w > 1e-9)) return null;
    const canvas = map.getCanvas();
    const cw = canvas.clientWidth || canvas.width;
    const ch = canvas.clientHeight || canvas.height;
    return { x: ((x / w + 1) / 2) * cw, y: ((1 - y / w) / 2) * ch };
  }

  render(gl: GL, args: CustomRenderMethodInput): void {
    if (!this.program || this.gl !== gl) {
      // context lost and restored (or swapped): rebuild program; chunk geometry is still held
      if (this.gl && this.gl !== gl) this.release();
      if (gl.isContextLost()) return;
      this.init(gl);
    }
    // mainMatrix maps web-mercator 0..1 (z in mercator units) to clip space (v5; modelViewProjectionMatrix is in world pixels)
    const M = (this.matrix ??= new Float64Array(16));
    M.set(args.defaultProjectionData.mainMatrix as ArrayLike<number>);
    let any = false;
    for (const c of this.chunks.values()) {
      if (c.count === 0) continue;
      if (c.dirty || !c.posBuf || !c.colBuf) {
        c.posBuf ??= gl.createBuffer();
        c.colBuf ??= gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, c.posBuf);
        gl.bufferData(gl.ARRAY_BUFFER, c.pos.subarray(0, c.count * 3), gl.DYNAMIC_DRAW);
        gl.bindBuffer(gl.ARRAY_BUFFER, c.colBuf);
        gl.bufferData(gl.ARRAY_BUFFER, c.col.subarray(0, c.count * 4), gl.DYNAMIC_DRAW);
        c.dirty = false;
      }
      any = true;
    }
    if (!any) return;
    gl.useProgram(this.program);
    gl.enableVertexAttribArray(this.aPos);
    gl.enableVertexAttribArray(this.aCol);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // colours are premultiplied
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.depthMask(false); // translucent cone/ribbon must not occlude each other
    gl.disable(gl.CULL_FACE);
    const mv = this.mv;
    // named batches (forecast ribbon / cone) first, the per-frame mesh (glyph, drop line, rings) over them: same
    // order as the old single mesh, so overlapping translucent colours blend identically
    for (let pass = 0; pass < 2; pass++) {
      for (const [id, c] of this.chunks) {
        if (c.count === 0 || (id === DYNAMIC) !== (pass === 1)) continue;
        // M · translate(origin + shift), in float64, then down to float32
        const tx = c.origin[0] + c.shift[0];
        const ty = c.origin[1] + c.shift[1];
        const tz = c.origin[2];
        for (let i = 0; i < 12; i++) mv[i] = M[i]!;
        for (let r = 0; r < 4; r++) mv[12 + r] = M[r]! * tx + M[4 + r]! * ty + M[8 + r]! * tz + M[12 + r]!;
        gl.uniformMatrix4fv(this.uMatrix, false, mv);
        gl.bindBuffer(gl.ARRAY_BUFFER, c.posBuf);
        gl.vertexAttribPointer(this.aPos, 3, gl.FLOAT, false, 0, 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, c.colBuf);
        gl.vertexAttribPointer(this.aCol, 4, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLES, 0, c.count);
      }
    }
    gl.disableVertexAttribArray(this.aPos);
    gl.disableVertexAttribArray(this.aCol);
  }
}
